import React from 'react'
import { cn } from '@/lib/cn'

type ButtonBaseProps = {
  variant?: 'primary' | 'ghost' | 'link'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
  to?: string // Add 'to' prop for Link compatibility
}

type ButtonAsButton = ButtonBaseProps & React.ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'button'
}

type ButtonAsAnchor = ButtonBaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  as: 'a'
  href: string
}

type ButtonAsComponent = ButtonBaseProps & {
  as: React.ElementType
  [key: string]: any // Allow other props like 'to'
}

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsComponent

const baseClasses = 'inline-flex items-center justify-center gap-2 font-bold transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed font-display uppercase tracking-widest'

const variantClasses: Record<string, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark shadow-xl shadow-primary/20 hover:-translate-y-0.5',
  ghost: 'bg-white/70 text-secondary border border-gray-200 hover:bg-white hover:border-gray-300',
  link: 'bg-transparent text-primary hover:underline px-0 active:scale-100'
}

const sizeClasses: Record<string, string> = {
  sm: 'px-5 py-2 text-sm rounded-full min-h-[36px]',
  md: 'px-8 py-3.5 text-base rounded-full min-h-[48px]',
  lg: 'px-10 py-4 text-base rounded-full min-h-[56px]'
}

function renderChildren(children: React.ReactNode, icon?: React.ReactNode) {
  if (!icon) return children
  return (
    <>
      <span className="btn-icon" aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </>
  )
}

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({
    as = 'button',
    variant = 'primary',
    size = 'md',
    fullWidth,
    icon,
    className,
    children,
    ...rest
  }, ref) => {
    const classes = cn(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth && 'w-full',
      className
    )

    if (as === 'button') {
      const buttonProps = rest as React.ButtonHTMLAttributes<HTMLButtonElement>
      return (
        <button ref={ref as React.Ref<HTMLButtonElement>} className={classes} {...buttonProps}>
          {renderChildren(children, icon)}
        </button>
      )
    }

    if (as === 'a') {
      const anchorProps = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...anchorProps}>
          {renderChildren(children, icon)}
        </a>
      )
    }

    const Component = as
    return (
      <Component ref={ref} className={classes} {...rest}>
        {renderChildren(children, icon)}
      </Component>
    )
  }
)

Button.displayName = 'Button'
